<template>
  <AccountLayout active-menu="orders">
    <div class="order-detail-page" v-loading="loading">
      <div class="detail-head">
        <div>
          <h1>Orders Detail</h1>
          <div class="order-title-line">
            <strong>{{ order?.order_no || '' }}</strong>
            <span>{{ order?.formatted_date || '' }}</span>
            <em>{{ order?.status_text_en || '' }}</em>
          </div>
          <div v-if="detail.tracking" class="tracking-line">
            <strong>{{ order?.tracking_number || '' }}</strong>
            <span>{{ detail.tracking }}</span>
          </div>
        </div>

        <div v-if="detail.step >= 0" class="detail-steps">
          <span v-for="(step, index) in steps" :key="step" :class="{ done: index <= detail.step }">
            <i></i>{{ step }}
          </span>
          <div v-if="detail.showInvoices" class="invoice-links">
            <a href="javascript:void(0)" @click.prevent="downloadInvoice('commercial')">
              <i class="el-icon-link"></i>
              Commercial Invoice
            </a>
            <a href="javascript:void(0)" @click.prevent="downloadInvoice('customer')">
              <i class="el-icon-link"></i>
              Customer Invoice
            </a>
          </div>
        </div>
      </div>

      <section-title>Shipping Address</section-title>
      <div class="info-box address-box">
        <span>Name :</span>
        <strong>{{ order?.shouhuo_json ? order.shouhuo_json.name : '-' }}</strong>
        <span>Phone :</span>
        <strong>{{ order?.shouhuo_json ? order.shouhuo_json.phone : '-' }}</strong>
        <span>Address :</span>
        <strong>{{ order?.shouhuo_json ? order.shouhuo_json.detail_address : '-' }}</strong>
      </div>

      <section-title>Shipping&nbsp;&nbsp; and Payment Methods</section-title>
      <div class="info-box method-box">
        <span>Shipping Methods :</span>
        <strong>{{ order?.shipping_method || '-' }}</strong>
        <span>Paying Methods :</span>
        <strong>{{ order?.payment_method || '-' }}</strong>
        <span>Ordering time :</span>
        <strong>{{ order?.formatted_date || order?.created_at || '-' }}</strong>
        <span>Paying time :</span>
        <strong>{{ order?.paid_at || '-' }}</strong>
      </div>

      <table class="detail-table">
        <thead>
          <tr>
            <th>Inquiry No.</th>
            <th>Description</th>
            <th>Qty</th>
            <th>Price, USD</th>
            <th>Sub-total, USD</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in order?.product_items || []" :key="index">
            <td v-if="index === 0" :rowspan="(order?.product_items || []).length">{{ order?.order_no || '' }}</td>
            <td>{{ item.name }}</td>
            <td>{{ item.num }}</td>
            <td>{{ item.price }}</td>
            <td>{{ item.subtotal }}</td>
          </tr>
          <tr>
            <td>Sub-total</td>
            <td colspan="3"></td>
            <td>{{ order?.subtotal ?? '-' }}</td>
          </tr>
        </tbody>
      </table>

      <div class="summary-box" :class="{ expanded: detail.showShippingInSummary }">
        <p>Subtotal {{ (order?.product_items || []).length }} items: &nbsp;{{ order?.subtotal ?? '-' }}</p>
        <template v-if="detail.showShippingInSummary">
          <p>Gross weight/ Kg : &nbsp;{{ order?.net_weight || '-' }}</p>
          <!-- <p>Dimensions: &nbsp;{{ order?.dimensions || '-' }}</p> -->
          <p>shipping fee: &nbsp;{{ order?.shipping_fee ?? '-' }}</p>
        </template>
        <p>Total: &nbsp;{{ order?.total ?? '-' }}</p>
      </div>

      <p v-if="detail.pendingReview" class="review-note">
        Your order is pending review. Please wait for the confirmation.
      </p>

      <template v-if="detail.showShippingFeeBlock">
        <section-title>Shipping fee</section-title>
        <div class="shipping-fee-box">
          <span>Gross weight/ Kg</span>
          <strong>{{ order?.net_weight || '-' }}</strong>
          <!-- <span>Dimensions</span>
          <strong>{{ order?.dimensions || '-' }}</strong> -->
          <span>Shipping fee</span>
          <strong>{{ order?.shipping_fee ?? '-' }}</strong>
        </div>
      </template>

      <template v-if="detail.showPayment">
        <section-title>Payment Methods</section-title>
        <div class="payment-box">
          <img :src="require('@/assets/\u7f16\u7ec4\u5907\u4efd 5.png')" alt="PayPal">
          <div class="payment-btn-wrap">
            <button type="button" :disabled="paying" @click="handlePayment">
              {{ paying ? 'Processing...' : detail.paymentButton }}
            </button>
            <!-- <button type="button" :disabled="paying" @click="handlePaymentMoNi" style="margin-left: 10px;">
              {{ paying ? 'Processing...' : detail.paymentButton }}
            </button> -->
          </div>
        </div>
        <p v-if="detail.paymentNote" class="payment-note">{{ detail.paymentNote }}</p>
      </template>

      <button v-if="detail.cancel" class="cancel-btn" type="button" @click="cancelOrder">
        Cancel Order
      </button>
      <button v-if="detail.actionButton" class="receipt-btn" type="button" @click="confirmReceipt">
        {{ detail.actionButton }}
      </button>

      <section class="history">
        <h2>History</h2>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Author</th>
              <th>Subject</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in historyRows" :key="index">
              <td>{{ row.date }}</td>
              <td>{{ row.author }}</td>
              <td><strong>{{ row.subject }}</strong></td>
              <td>
                {{ row.action }}
                <small v-if="row.note">{{ row.note }}</small>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  </AccountLayout>
</template>

<script>
import AccountLayout from '@/components/common/AccountLayout.vue'
import { getToken } from '@/util/token-util.js'
import { API_ROOT } from '@/config/env.js'

export default {
  name: 'OrderDetailPage',
  components: {
    AccountLayout,
    SectionTitle: {
      functional: true,
      render(h, context) {
        return h('div', { class: 'section-title' }, context.children)
      }
    }
  },
  data() {
    return {
      steps: ['Unpaid', 'Waiting Shipping Fee', 'Pay shipping fee', 'Waiting for dispatch', 'Pending receipt', 'Completed'],
      order: null,
      loading: false,
      paying: false
    }
  },
  async mounted() {
    if (!getToken()) {
      this.$message.warning('Please log in first')
      this.$router.push({ name: 'home' })
      return
    }
    const id = this.$route.query.id
    if (!id) {
      this.$message.warning('Order ID is required')
      this.$router.push({ name: 'orders' })
      return
    }
    await this.loadOrder()

    // Payment callback: reload the order and clear callback parameters.
    if (this.$route.query.pay_success == 1) {
      this.$message.success('Payment successful')
      await this.loadOrder()
      // Clear callback parameters from URL
      const query = { ...this.$route.query }
      delete query.pay_success
      delete query.pay_cancelled
      this.$router.replace({ query })
    }
  },
  computed: {
    detail() {
      if (!this.order) {
        return {
          step: -1,
          tracking: '',
          cancel: false,
          pendingReview: false,
          showPayment: false,
          showShippingFeeBlock: false,
          showShippingInSummary: false,
          showInvoices: false,
          actionButton: null,
          paymentButton: null,
          paymentNote: '',
        }
      }
      const o = this.order
      const status = o.status
      const stepMap = { 2: 0, 3: 1, 4: 2, 5: 3, 6: 4, 7: 5 }
      const step = stepMap[status] !== undefined ? stepMap[status] : -1
      return {
        step,
        tracking: o.tracking_number ? (o.tracking_company || 'Tracking') : '',
        cancel: status === 1,
        pendingReview: status === 1,
        showPayment: status === 2 || status === 4,
        showShippingFeeBlock: status === 4,
        showShippingInSummary: status === 5 || status === 6 || status === 7,
        showInvoices: status === 7,
        actionButton: status === 6 ? 'Confirm Receipt' : null,
        paymentButton: status === 2 ? 'Pay Now' : (status === 4 ? 'Pay Shipping fee' : null),
        paymentNote: status === 2 ? 'Logistics will calculate shipping costs when items are shipped, send you the bill, and you may complete the payment afterward.' : '',
      }
    },
    historyRows() {
      if (!this.order || !this.order.history_json) return []
      return this.order.history_json.map(row => ({
        date: row.date,
        author: row.author,
        subject: row.subject,
        action: row.action,
        note: row.action_remark || row.note || ''
      }))
    }
  },
  methods: {
    async loadOrder() {
      this.loading = true
      try {
        const res = await this.$api({
          url: 'Order/detail',
          method: 'get',
          data: { id: this.$route.query.id }
        })
        if (res.code === 200) {
          this.order = res.data
        }
      } catch (e) {
        this.$message.error('Failed to load order detail')
      } finally {
        this.loading = false
      }
    },
    async handlePayment() {
      if (!this.order) return
      const status = this.order.status
      // status 2: Unpaid (Pay Now) -> Paypal/createPayment
      // status 4: Pay Shipping fee -> Paypal/createShippingPayment
      const isShippingPay = status === 4
      const url = isShippingPay ? 'Paypal/createShippingPayment' : 'Paypal/createPayment'
      const origin = window.location.origin
      const redirectBase = `${origin}/orders/detail?id=${this.order.id}`
      const returnUrl = `${redirectBase}&pay_success=1`
      const cancelUrl = `${redirectBase}&pay_cancelled=1`
      this.paying = true
      try {
        const res = await this.$api({
          url,
          method: 'post',
          data: {
            id: this.order.id,
            return_url: returnUrl,
            cancel_url: cancelUrl
          }
        })
        if (res.code === 200 && res.data && res.data.approve_url) {
          this.loadOrder()
          window.location.href = res.data.approve_url
        } else {
          this.$message.error(res.message || 'Failed to create payment')
        }
      } catch (e) {
        this.$message.error('Failed to create payment')
      } finally {
        this.paying = false
      }
    },
    async handlePaymentMoNi() {
      if (!this.order) return
      const status = this.order.status
      const isShippingPay = status === 4
      const url = 'Paypal/mockPayment'
      this.paying = true
      try {
        const res = await this.$api({
          url,
          method: 'post',
          data: {
            id: this.order.id,
            type: isShippingPay ? 'shipping' : 'product',
          }
        })
        if (res.code === 200) {
          this.loadOrder()
        } else {
          this.$message.error(res.message || 'Failed to create payment')
        }
      } catch (e) {
        this.$message.error('Failed to create payment')
      } finally {
        this.paying = false
      }
    },
    confirmReceipt() {
      if (!this.order) return
      this.$confirm('Are you sure to confirm receipt of this order?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$api({
            url: 'Order/confirmReceipt',
            method: 'post',
            data: { id: this.order.id }
          })
          if (res.code === 200) {
            this.$message.success('Receipt confirmed')
            await this.loadOrder()
          } else {
            this.$message.error(res.message || 'Failed to confirm receipt')
          }
        } catch (e) {
          this.$message.error('Failed to confirm receipt')
        }
      }).catch(() => { })
    },
    cancelOrder() {
      if (!this.order) return
      this.$confirm('Are you sure to cancel this order?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$api({
            url: 'Order/cancel',
            method: 'post',
            data: { id: this.order.id }
          })
          if (res.code === 200) {
            this.$message.success('Order cancelled')
            await this.loadOrder()
          } else {
            this.$message.error(res.message || 'Failed to cancel order')
          }
        } catch (e) {
          this.$message.error('Failed to cancel order')
        }
      }).catch(() => { })
    },
    async downloadInvoice(type) {
      if (!this.order) return
      let invoiceUrl = `${API_ROOT}/api/Order/invoice?id=${this.order.id}&type=${type}&token=${getToken()}`
      window.open(invoiceUrl, '_blank')
    }
  }
}
</script>

<style scoped>
.order-detail-page {
  color: #333;
}

.detail-head {
  min-height: 118px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 36px;
}

.detail-head h1 {
  margin: 0 0 28px;
  color: #111;
  font-size: 26px;
  font-weight: 400;
}

.order-title-line,
.tracking-line {
  display: flex;
  align-items: center;
  gap: 28px;
  color: #777;
  font-size: 13px;
}

.tracking-line {
  margin-top: 14px;
}

.order-title-line strong,
.tracking-line strong {
  color: #F34B01;
  font-style: normal;
}

.order-title-line em {
  color: #D10000;
  font-style: normal;
}

.detail-steps {
  width: 450px;
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
}

.detail-steps span {
  position: relative;
  color: #aaa;
  text-align: center;
  font-size: 11px;
  line-height: 1.15;
}

.detail-steps span::before {
  content: '';
  position: absolute;
  top: 7px;
  left: -50%;
  right: 50%;
  height: 3px;
  background: #bbb;
  z-index: 0;
}

.detail-steps span:first-child::before {
  display: none;
}

.detail-steps i {
  position: relative;
  z-index: 1;
  width: 14px;
  height: 14px;
  margin: 0 auto 5px;
  display: block;
  border-radius: 50%;
  background: #bbb;
}

.detail-steps span.done,
.detail-steps span.done i {
  color: #F34B01;
}

.detail-steps span.done i,
.detail-steps span.done::before {
  background: #F34B01;
}

.invoice-links {
  grid-column: 2 / 6;
  margin-top: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 58px;
  white-space: nowrap;
}

.invoice-links a {
  color: #F34B01;
  text-decoration: none;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
}

.invoice-links i {
  margin: 0;
  background: transparent;
  margin-right: 8px;
  font-size: 15px;
}

.section-title {
  height: 42px;
  margin-top: 16px;
  padding: 0 26px;
  display: flex;
  align-items: center;
  border-radius: 2px;
  background: #fafbfc;
  color: #111;
  font-size: 16px;
}

.info-box {
  margin-top: 14px;
  padding: 22px 26px;
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  box-sizing: border-box;
  font-size: 14px;
}

.address-box {
  display: grid;
  grid-template-columns: 78px 1fr 78px 1fr;
  gap: 20px 26px;
}

.method-box {
  display: grid;
  grid-template-columns: 140px 1fr 140px 1fr;
  gap: 20px 18px;
}

.info-box span {
  min-width: 130px;
  max-width: 160px;
  color: #555;
}

.info-box strong {
  color: #333;
  font-weight: 400;
}

.detail-table {
  width: 100%;
  margin-top: 16px;
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  border-collapse: separate;
  border-spacing: 0;
  overflow: hidden;
  color: #333;
  font-size: 12px;
}

.detail-table th,
.detail-table td {
  height: 56px;
  border-right: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  text-align: center;
  font-weight: 400;
}

.detail-table th {
  height: 42px;
  background: #fafbfc;
}

.detail-table td:nth-child(2),
.detail-table tr:nth-child(2) td:first-child {
  color: #F34B01;
}

.detail-table tbody tr:last-child td:first-child {
  color: inherit;
}

.detail-table td:last-child,
.detail-table th:last-child {
  border-right: 0;
}

.detail-table tr:last-child td {
  border-bottom: 0;
}

.summary-box {
  min-height: 94px;
  margin-top: 12px;
  padding: 22px 42px;
  background: #fafbfc;
  text-align: right;
  font-family: inherit;
  font-weight: 400;
  font-size: 16px;
  color: #000000;
  line-height: 22px;
  box-sizing: border-box;
}

.summary-box.expanded {
  min-height: 145px;
}

.summary-box p {
  margin: 0;
}

.shipping-fee-box {
  width: 330px;
  margin: 18px 0 28px 26px;
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 18px 24px;
  font-family: inherit;
  font-weight: 400;
  font-size: 16px;
  color: #000000;
  line-height: 22px;
}

.shipping-fee-box span {
  color: #333;
}

.shipping-fee-box strong {
  font-weight: 400;
}

.payment-box {
  min-height: 90px;
  padding: 22px 26px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-box img {
  width: 126px;
  height: auto;
}

.payment-btn-wrap {
  display: flex;
  justify-content: flex-end;
}

.payment-box button,
.receipt-btn,
.cancel-btn {
  width: 132px;
  height: 36px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
}

.payment-box button,
.receipt-btn {
  border: 0;
  background: #F34B01;
  color: #fff;
}

.review-note {
  height: 42px;
  margin: 12px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff1ed;
  color: #F34B01;
  font-size: 13px;
}

.payment-note {
  height: 42px;
  margin: 0 0 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff1ed;
  color: #F34B01;
  font-size: 13px;
}

.receipt-btn,
.cancel-btn {
  margin: 28px 0 0 auto;
  display: block;
}

.cancel-btn {
  border: 1px solid #F34B01;
  background: #fff;
  color: #F34B01;
}

.history {
  margin-top: 56px;
}

.history h2 {
  margin: 0 0 34px;
  color: #111;
  font-size: 18px;
  font-weight: 400;
}

.history table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.history th,
.history td {
  height: 68px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.history th {
  height: 42px;
  border-bottom: 1px solid #F34B01;
  color: #111;
  font-size: 16px;
}

.history th,
.history td {
  padding: 0 30px;
}

.history strong {
  font-weight: 600;
}

.history small {
  margin-top: 6px;
  display: block;
  color: #aaa;
  font-size: 12px;
}

@media (max-width: 960px) {
  .detail-head {
    display: block;
  }

  .detail-steps {
    width: 100%;
    margin: 28px 0 10px;
  }

  .address-box,
  .method-box {
    grid-template-columns: 120px 1fr;
  }

  .payment-box {
    align-items: flex-start;
  }
}
</style>
